import React, { useState, useMemo, useEffect } from 'react';
import { menuStructure, ROLE_LEVEL } from '../data/menuStructure';
import { getPinned, togglePinned, getRecent } from '../data/storage';
import Icon from './Icon';

function DesignSidebar({
  currentPage, sidebarCollapsed, mobileOpen, onNavClick, onToggleCollapse,
  userRole, incidentCount, incidentInProgress, incidentInvestigating,
  busyWorkers, workerOnline, pendingTasks, runningTasks, failedTasks,
  execRunningCount, vulnCritical, vulnHigh, vulnOthers,
}) {
  const userLevel = ROLE_LEVEL[userRole] ?? -1;
  const [pinned, setPinned] = useState(getPinned());
  const [expanded, setExpanded] = useState({});
  const [recent] = useState(getRecent());

  const isAllowed = (item) => {
    if (item.hideIf && item.hideIf(userLevel)) return false;
    return userLevel >= (ROLE_LEVEL[item.minRole] ?? 99);
  };

  const flatItems = useMemo(() => {
    const all = [];
    for (const g of menuStructure) {
      for (const i of g.items) {
        all.push({ ...i, category: g.category });
        if (i.children) {
          for (const c of i.children) all.push({ ...c, category: g.category });
        }
      }
    }
    return all;
  }, []);

  const pinnedItems = useMemo(
    () => pinned.map((id) => flatItems.find((i) => i.id === id)).filter(Boolean).filter(isAllowed).slice(0, 6),
    [pinned, flatItems, userLevel]
  );

  const recentItems = useMemo(
    () => recent
      .filter((id) => !pinned.includes(id))
      .map((id) => flatItems.find((i) => i.id === id))
      .filter(Boolean)
      .filter(isAllowed)
      .slice(0, 5),
    [recent, pinned, flatItems, userLevel]
  );

  const groupFor = (page) =>
    menuStructure.find((g) => g.items.some((i) => i.id === page || i.children?.some((c) => c.id === page)));

  let activeGroup = groupFor(currentPage);
  if (!activeGroup) {
    if (currentPage.startsWith('servers/')) activeGroup = groupFor('servers');
    else if (currentPage.startsWith('devices/')) activeGroup = groupFor('devices');
  }
  if (!activeGroup) activeGroup = menuStructure[0];

  useEffect(() => {
    const parents = activeGroup?.items.filter((i) => i.children?.some((c) => c.id === currentPage)) || [];
    if (parents.length > 0) {
      setExpanded((prev) => ({ ...prev, [parents[0].id]: true }));
    }
  }, [currentPage, activeGroup]);

  const visibleGroupItems = useMemo(
    () => activeGroup.items.filter(isAllowed),
    [activeGroup, userLevel]
  );

  const renderBadge = (item) => {
    if (item.id === 'incidents') {
      const counts = [
        ['T', incidentCount ?? 0, '#a5acc9'],
        ['P', incidentInProgress ?? 0, '#fbbf24'],
        ['I', incidentInvestigating ?? 0, '#fb923c'],
      ];
      return (
        <span style={{ display: 'inline-flex', gap: 4, marginLeft: 'auto' }}>
          {counts.map(([k, v, color]) => (
            <span key={k} className="count" style={{ marginLeft: 0, color }}>{k}{v}</span>
          ))}
        </span>
      );
    }
    if (item.id === 'workers') {
      const busy = busyWorkers ?? 0;
      const online = workerOnline ?? 0;
      let color = '#34d399';
      if (online === 0) color = '#6b7396';
      else if (busy >= online) color = '#f87171';
      else if (busy > 0) color = '#fb923c';
      return <span className="count" style={{ marginLeft: 'auto', color }}>{busy}/{online}</span>;
    }
    if (item.id === 'tasks') {
      const counts = [
        ['P', pendingTasks ?? 0, '#fbbf24'],
        ['R', runningTasks ?? 0, '#60a5fa'],
        ['F', failedTasks ?? 0, '#f87171'],
      ];
      return (
        <span style={{ display: 'inline-flex', gap: 4, marginLeft: 'auto' }}>
          {counts.map(([k, v, color]) => (
            <span key={k} className="count" style={{ marginLeft: 0, color }}>{k}{v}</span>
          ))}
        </span>
      );
    }
    if (item.id === 'executions' && (execRunningCount ?? 0) > 0) {
      return <span className="count" style={{ marginLeft: 'auto', color: '#60a5fa' }}>{execRunningCount}</span>;
    }
    if (item.id === 'vulnerabilities') {
      const counts = [
        ['C', vulnCritical ?? 0, '#f87171'],
        ['H', vulnHigh ?? 0, '#fb923c'],
        ['O', vulnOthers ?? 0, '#a5acc9'],
      ];
      return (
        <span style={{ display: 'inline-flex', gap: 4, marginLeft: 'auto' }}>
          {counts.map(([k, v, color]) => (
            <span key={k} className="count" style={{ marginLeft: 0, color }}>{k}{v}</span>
          ))}
        </span>
      );
    }
    return null;
  };

  const handlePin = (e, id) => {
    e.stopPropagation();
    e.preventDefault();
    setPinned(togglePinned(id));
  };

  const renderItem = (item, opts = {}) => {
    const { isChild = false, allowPin = true, stripChildren = false } = opts;
    const active = currentPage === item.id;
    const childActive = item.children?.some((c) => c.id === currentPage);
    const hasChildren = !stripChildren && item.children?.length > 0;
    const visibleChildren = hasChildren ? item.children.filter(isAllowed) : [];
    const isOpen = !!expanded[item.id];
    const isPinned = pinned.includes(item.id);

    const click = (e) => {
      e.preventDefault();
      if (item.groupOnly || hasChildren) {
        setExpanded((prev) => ({ ...prev, [item.id]: !prev[item.id] }));
      } else {
        onNavClick(item.id);
      }
    };

    return (
      <div key={item.id}>
        <a
          className={`side-item ${hasChildren ? 'side-parent' : ''} ${isOpen ? 'open' : ''} ${active || childActive ? 'active' : ''}`}
          href={`#${item.id}`}
          onClick={click}
          title={item.label}
        >
          <span className="icon"><Icon name={item.icon} size={16} /></span>
          <span className="lbl">{item.label}</span>
          {hasChildren && <span className="chev"><Icon name="chevron-right" size={12} /></span>}
          {renderBadge(item)}
          {allowPin && !isChild && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => handlePin(e, item.id)}
              onKeyDown={(e) => { if (e.key === 'Enter') handlePin(e, item.id); }}
              title={isPinned ? 'Unpin' : 'Pin to top'}
              style={{ opacity: isPinned ? 1 : undefined, color: isPinned ? '#fbbf24' : undefined }}
              className="group-hover:opacity-100 opacity-0 transition-opacity"
            >
              <Icon name="pin" size={11} />
            </span>
          )}
        </a>
        {hasChildren && (
          <div className={`side-children ${isOpen ? 'open' : ''}`}>
            {visibleChildren.map((c) => renderItem(c, { isChild: true }))}
          </div>
        )}
      </div>
    );
  };

  return (
    <aside className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${mobileOpen ? 'open' : ''}`} id="sidebar">
      {pinnedItems.length > 0 && (
        <>
          <div className="side-label">Pinned</div>
          <div className="side-group">
            {pinnedItems.map((item) => renderItem({ ...item, children: undefined }))}
          </div>
        </>
      )}
      {recentItems.length > 0 && !sidebarCollapsed && (
        <>
          <div className="side-label">Recent</div>
          <div className="side-group">
            {recentItems.map((item) => renderItem({ ...item, children: undefined }))}
          </div>
        </>
      )}
      <div className="side-label">{activeGroup.category}</div>
      <div className="side-group">
        {visibleGroupItems.map((item) => renderItem(item))}
        {visibleGroupItems.length === 0 && (
          <div className="side-sub">No items for your role</div>
        )}
      </div>
    </aside>
  );
}

export default DesignSidebar;
